/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// redux utils
import { createSlice } from '@reduxjs/toolkit'
// thunks
import { fetchPageview, fetchPageviewForCsv, fetchPrebidRevenue, fetchPrebidRevenueForCsv, fetchPublisherRevenue, fetchPublisherRevenueForCsv, fetchVideoPerformance, fetchVideoPerformanceForCsv } from '../thunks/revenue_beta'
import {
  INITIAL_STATE,
  NET_REVENUE,
  GROSS_REVENUE,
  TOTAL_IMPRESSIONS,
  INITIAL_IMPRESSIONS,
  REFRESH_IMPRESSIONS,
  PAGE_VIEWS,
  AVG_RPM,
  AVG_CPM,
  CTR,
  VIEWABILITY,
  INITIAL_VIEWABILITY,
  REFRESH_VIEWABILITY,
  METRIC_UNIT,
  DIMENSION_DROPDOWN_STATE,
  IMPRESSION_PER_PAGEVIEW,
  VIDEO_COMPLETION_RATE,
  AD_REQUEST,
  BID_RATE,
  WIN_RATE
} from '../../pages/dashboard/monetization/consts'

/* ========== ~~~~~~~~~~ REVENUE SLICE : REDUCER ~~~~~~~~~~ ========== */
/*
    The keys in this state model reflects available metrics from RMS
   */
const initialState = {
  lastUpdated: null,
  [NET_REVENUE]: { ...INITIAL_STATE },
  [GROSS_REVENUE]: { ...INITIAL_STATE },
  [TOTAL_IMPRESSIONS]: { ...INITIAL_STATE },
  [INITIAL_IMPRESSIONS]: { ...INITIAL_STATE },
  [REFRESH_IMPRESSIONS]: { ...INITIAL_STATE },
  [IMPRESSION_PER_PAGEVIEW]: { ...INITIAL_STATE },
  [PAGE_VIEWS]: { ...INITIAL_STATE },
  [AVG_RPM]: { ...INITIAL_STATE },
  [AVG_CPM]: { ...INITIAL_STATE },
  [CTR]: { ...INITIAL_STATE },
  [VIEWABILITY]: { ...INITIAL_STATE },
  [INITIAL_VIEWABILITY]: { ...INITIAL_STATE },
  [REFRESH_VIEWABILITY]: { ...INITIAL_STATE },
  [VIDEO_COMPLETION_RATE]: { ...INITIAL_STATE },
  [AD_REQUEST]: { ...INITIAL_STATE },
  [BID_RATE]: { ...INITIAL_STATE },
  [WIN_RATE]: { ...INITIAL_STATE },
  selectedPeriod: {},
  metricUnit: { ...METRIC_UNIT },
  dimension: { ...DIMENSION_DROPDOWN_STATE }
}

export const revenueBetaSlice = createSlice({
  name: 'revenueBeta',
  initialState,
  reducers: {
    refresh: () => initialState,
    setDatePeriod: (state, action) => {
      const { metric, period } = action.payload
      state.selectedPeriod[metric] = period
    },
    setDatePeriodForAll: (state, action) => {
      Object.keys(state).forEach(metric => {
        state.selectedPeriod[metric] = action.payload
      })
    },
    resetDatePeriod: (state) => {
      state.selectedPeriod = {}
    },
    setDimensionForAll: (state, action) => {
      Object.keys(state.dimension).forEach(metric => {
        if (!state.dimension[metric].second) state.dimension[metric].first = action.payload
      })
    },
    setDimension: (state, action) => {
      const { metric, dimension, order } = action.payload
      state.dimension[metric][order] = dimension
    },
    resetDimension: (state) => {
      state.dimension = { ...DIMENSION_DROPDOWN_STATE }
    },
    resetSecondDimension: (state, action) => {
      const metric = action.payload
      state.dimension[metric].second = null
    },
    resetCsvDownloadStatus: (state, action) => {
      const metric = action.payload
      state[metric].csvdatastatus = 'pending'
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPublisherRevenue.pending, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric] = { ...INITIAL_STATE }
          state[metric].status = 'pending'
        })
      })
      .addCase(fetchPublisherRevenue.fulfilled, (state, action) => {
        const { latestUpdatedAt } = action.payload
        Object.keys(action.payload).forEach(metric => {
          if (state[metric]) {
            state[metric] = { ...state[metric], ...action.payload[metric] }
            state[metric].status = 'fulfilled'
            state[metric].csvDownloadLinkParams = action.meta.arg
          }
        })
        state.lastUpdated = latestUpdatedAt
      })
      .addCase(fetchPublisherRevenue.rejected, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric] = { ...INITIAL_STATE }
          state[metric].status = 'rejected'
        })
      })
      .addCase(fetchPrebidRevenue.pending, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric] = { ...INITIAL_STATE }
          state[metric].status = 'pending'
        })
      })
      .addCase(fetchPrebidRevenue.fulfilled, (state, action) => {
        const { latestUpdatedAt } = action.payload
        Object.keys(action.payload).forEach(metric => {
          if (state[metric]) {
            state[metric] = { ...state[metric], ...action.payload[metric] }
            state[metric].status = 'fulfilled'
            state[metric].csvDownloadLinkParams = action.meta.arg
          }
        })
        state.lastUpdated = latestUpdatedAt
      })
      .addCase(fetchPrebidRevenue.rejected, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric] = { ...INITIAL_STATE }
          state[metric].status = 'rejected'
        })
      })
      .addCase(fetchVideoPerformance.pending, (state, action) => {
        state[VIDEO_COMPLETION_RATE] = { ...INITIAL_STATE }
        state[VIDEO_COMPLETION_RATE].status = 'pending'
      })
      .addCase(fetchVideoPerformance.fulfilled, (state, action) => {
        state[VIDEO_COMPLETION_RATE] = action.payload[VIDEO_COMPLETION_RATE]
        Object.keys(state[TOTAL_IMPRESSIONS]).forEach(period => {
          if (period !== 'status' && period !== 'csvdata' && period !== 'csvdatastatus' && period !== 'csvDownloadLinkParams') {
            state[TOTAL_IMPRESSIONS][period] = {
              ...state[TOTAL_IMPRESSIONS][period],
              [VIDEO_COMPLETION_RATE]: action.payload[VIDEO_COMPLETION_RATE][period].TOTAL
            }
          }
        })
        state[VIDEO_COMPLETION_RATE].status = 'fulfilled'
      })
      .addCase(fetchVideoPerformance.rejected, (state, action) => {
        state[VIDEO_COMPLETION_RATE] = { ...INITIAL_STATE }
        state[VIDEO_COMPLETION_RATE].status = 'rejected'
      })
      .addCase(fetchPageview.pending, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric] = { ...INITIAL_STATE }
          state[metric].status = 'pending'
        })
      })
      .addCase(fetchPageview.fulfilled, (state, action) => {
        const { latestUpdatedAt } = action.payload
        Object.keys(action.payload).forEach(metric => {
          if (state[metric]) {
            state[metric] = { ...state[metric], ...action.payload[metric] }
            state[metric].status = 'fulfilled'
            state[metric].csvDownloadLinkParams = action.meta.arg
          }
        })
        if (action.payload[IMPRESSION_PER_PAGEVIEW]) {
          [TOTAL_IMPRESSIONS, INITIAL_IMPRESSIONS, REFRESH_IMPRESSIONS].forEach(metric => {
            Object.keys(state[metric]).forEach(period => {
              if (period !== 'status' && period !== 'csvdata' && period !== 'csvdatastatus' && period !== 'csvDownloadLinkParams') state[metric][period][IMPRESSION_PER_PAGEVIEW] = action.payload[IMPRESSION_PER_PAGEVIEW][period].TOTAL
            })
          })
        }
        state.lastUpdated = latestUpdatedAt
      })
      .addCase(fetchPageview.rejected, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric] = { ...INITIAL_STATE }
          state[metric].status = 'rejected'
        })
      })
      .addCase(fetchPublisherRevenueForCsv.pending, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric].csvdata = {}
          state[metric].csvdatastatus = 'pending'
        })
      })
      .addCase(fetchPublisherRevenueForCsv.fulfilled, (state, action) => {
        const { latestUpdatedAt } = action.payload
        Object.keys(action.payload).forEach(metric => {
          if (state[metric]) {
            state[metric].csvdata = { ...state[metric], ...action.payload[metric] }
            state[metric].csvdatastatus = 'fulfilled'
          }
        })
        state.lastUpdated = latestUpdatedAt
      })
      .addCase(fetchPublisherRevenueForCsv.rejected, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric].csvdata = {}
          state[metric].csvdatastatus = 'rejected'
        })
      })
      .addCase(fetchPageviewForCsv.pending, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric].csvdata = {}
          state[metric].csvdatastatus = 'pending'
        })
      })
      .addCase(fetchPageviewForCsv.fulfilled, (state, action) => {
        const { latestUpdatedAt } = action.payload
        Object.keys(action.payload).forEach(metric => {
          if (state[metric]) {
            state[metric].csvdata = { ...state[metric], ...action.payload[metric] }
            state[metric].csvdatastatus = 'fulfilled'
          }
        })
        if (action.payload[IMPRESSION_PER_PAGEVIEW]) {
          [TOTAL_IMPRESSIONS, INITIAL_IMPRESSIONS, REFRESH_IMPRESSIONS].forEach(metric => {
            Object.keys(state[metric]).forEach(period => {
              if (period !== 'status' && period !== 'csvdata' && period !== 'csvdatastatus' && period !== 'csvDownloadLinkParams') state[metric].csvdata[period][IMPRESSION_PER_PAGEVIEW] = action.payload[IMPRESSION_PER_PAGEVIEW][period].TOTAL
            })
          })
        }
        state.lastUpdated = latestUpdatedAt
      })
      .addCase(fetchPageviewForCsv.rejected, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric].csvdata = {}
          state[metric].csvdatastatus = 'rejected'
        })
      })
      .addCase(fetchVideoPerformanceForCsv.pending, (state, action) => {
        state[VIDEO_COMPLETION_RATE].csvdata = {}
        state[VIDEO_COMPLETION_RATE].csvdatastatus = 'pending'
      })
      .addCase(fetchVideoPerformanceForCsv.fulfilled, (state, action) => {
        state[VIDEO_COMPLETION_RATE].csvdata = action.payload[VIDEO_COMPLETION_RATE]
        Object.keys(state[TOTAL_IMPRESSIONS]).forEach(period => {
          if (period !== 'status' && period !== 'csvdata' && period !== 'csvdatastatus' && period !== 'csvDownloadLinkParams') {
            state[TOTAL_IMPRESSIONS].csvdata[period] = {
              ...state[TOTAL_IMPRESSIONS][period],
              [VIDEO_COMPLETION_RATE]: action.payload[VIDEO_COMPLETION_RATE][period].TOTAL
            }
          }
        })
        state[VIDEO_COMPLETION_RATE].csvdatastatus = 'fulfilled'
      })
      .addCase(fetchVideoPerformanceForCsv.rejected, (state, action) => {
        state[VIDEO_COMPLETION_RATE].csvdata = {}
        state[VIDEO_COMPLETION_RATE].csvdatastatus = 'rejected'
      })
      .addCase(fetchPrebidRevenueForCsv.pending, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric].csvdata = {}
          state[metric].csvdatastatus = 'pending'
        })
      })
      .addCase(fetchPrebidRevenueForCsv.fulfilled, (state, action) => {
        const { latestUpdatedAt } = action.payload
        Object.keys(action.payload).forEach(metric => {
          if (state[metric]) {
            state[metric].csvdata = { ...state[metric], ...action.payload[metric] }
            state[metric].csvdatastatus = 'fulfilled'
          }
        })
        state.lastUpdated = latestUpdatedAt
      })
      .addCase(fetchPrebidRevenueForCsv.rejected, (state, action) => {
        const { meta } = action
        meta.arg.metrics.forEach(metric => {
          state[metric].csvdata = {}
          state[metric].csvdatastatus = 'rejected'
        })
      })
  }
})

export const { setDatePeriod, refresh, resetDatePeriod, resetDimension, setDatePeriodForAll, setDimension, setDimensionForAll, resetSecondDimension, resetCsvDownloadStatus } = revenueBetaSlice.actions
export default revenueBetaSlice.reducer
